import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { GeneratePage, SchedulerProvider, CmsComponentsMap } from '@ui/generatePage';
import { useDeviceType } from '@ui/utils/useDeviceType';
import { selectDeviceLayout } from './selectDeviceLayout';
import { buildCmsPageMeta } from './buildCmsPageMeta';
import { useCmsPageLayoutQuery, useSchedulerStatusQuery } from '@data/cms';
import { useTenantConfigQuery } from '@data/tenants';
import { NotFoundPage } from '../NotFoundPage';
import { Loader } from '@ui/common/components/Loader/Loader';
import { InstallPwa } from '@ui/common/components/InstallPwa/InstallPwa';
import { CrossFade } from '@ui/common/components/CrossFade';

/**
 * SC-1213: the component registry arrives as a prop rather than being imported here.
 *
 * This module is shared — `App.tsx` and `ServerApp.tsx` import the very same file, so
 * unlike the other routes there is no lazy/static pair to make a "server version" of.
 * Importing either map here would fix the registry for both callers; importing the
 * static one would additionally pull all 37 components into the client bundle.
 */
type CmsPageProps = {
  componentsMap: CmsComponentsMap;
};

export const CmsPage = ({ componentsMap }: CmsPageProps) => {
  const location = useLocation();
  const deviceType = useDeviceType();
  const { data: tenantConfig } = useTenantConfigQuery();

  const { data: pageData, isLoading, error } = useCmsPageLayoutQuery(location.pathname);
  const { data: schedulerStatus } = useSchedulerStatusQuery();

  // Extracted to `selectDeviceLayout` so the fallback rules are testable — they were four
  // near-identical inline branches with no coverage. Behaviour is unchanged; see its tests.
  const layout = useMemo(() => selectDeviceLayout(pageData, deviceType), [deviceType, pageData]);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 100px)',
        }}
      >
        <Loader />
      </div>
    );
  }

  // No layout for this device — either the page does not exist, or (on desktop, which
  // deliberately does not fall back) only a mobile/tablet layout was authored. Previously
  // this reached GeneratePage as `undefined` and threw on `rootChildren.map`.
  if (error || !layout) {
    return <NotFoundPage />;
  }

  const { metaTitle, metaDescription } = buildCmsPageMeta(pageData?.meta, tenantConfig?.env);

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <InstallPwa />
      <SchedulerProvider status={schedulerStatus ?? null}>
        <CrossFade variantKey={deviceType} className="cms-page-cross-fade">
          <GeneratePage {...layout} componentsMap={componentsMap} />
        </CrossFade>
      </SchedulerProvider>
    </>
  );
};
